import {
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { ConnectionStatus, Network } from '@capacitor/network';
import { IonToast } from '@ionic/angular/standalone';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Logger } from '@app/utils/logger';
import { Session } from '@providers/session/session.service';

/**
 * Network Component.
 *
 * @exports
 * @class NetworkComponent
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-network',
  templateUrl: 'network.component.html',
  imports: [IonToast]
})
export class NetworkComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly session = inject(Session);
  private readonly zone = inject(NgZone);

  private readonly listOfUrlForOfflineToastIsNotAllowed = signal<Array<string>>(
    ['/assistant']
  );
  private readonly offlineToastAllowed = signal<boolean>(true);

  offline = new BehaviorSubject(false);

  isOffline = toSignal(this.offline);

  logger = Logger.getInstance(NetworkComponent.name);

  destroyed = new Subject<boolean>();

  ableToOpenOfflineToast = computed(
    () => this.isOffline() && this.offlineToastAllowed()
  );

  /**
   * Get network status.
   *
   * @memberof NetworkComponent
   */
  ngOnInit(): void {
    this.updateStatus();

    Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      this.zone.run(() => {
        this.updateStatus(status);
      });
    });

    this.router.events
      .pipe(debounceTime(500), takeUntil(this.destroyed))
      .subscribe({
        /**
         * Set app bg color when route change.
         */
        next: (v) => {
          if (v instanceof NavigationEnd) {
            const url = v.url.split('?')[0];
            const isBlocked = this.listOfUrlForOfflineToastIsNotAllowed().some(
              (item) => item === url
            );
            this.offlineToastAllowed.set(!isBlocked);

            this.updateStatus(undefined, false);
          }
        }
      });
  }

  /**
   * Cleanup.
   *
   * @memberof NetworkComponent
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
    this.offline.complete();
    Network.removeAllListeners();
  }

  /**
   * Update network status and store it to local-storage.
   *
   * @returns {void}
   */
  updateStatus(status?: ConnectionStatus, log = true): void {
    const logger = this.logger.child(this.updateStatus.name);

    Network.getStatus()
      .then((value) => {
        status = status ?? value;

        this.offline.next(!status.connected);
        this.session.store(
          this.session.keys.networkConnected,
          status.connected
        );

        if (log) {
          logger.debug('status changed', status);
        }
      })
      .catch(logger.error);
  }
}
